/**
 * Gift Sleeves Web Component
 * 
 * Attributes:
 * - selected-variant-id: The ID of the currently selected gift wrap variant.
 * - is-incart: Boolean attribute indicating if the component is used in a cart item context.
 * 
 * Events:
 * - gift-wrap:change: Fired when the selection changes. Detail includes variantId.
 */
class GiftSleeves extends HTMLElement {
  static get observedAttributes() {
    return ['selected-variant-id'];
  }

  constructor() {
    super();
    this.selectors = {
      checkbox: '[js-gift-wrap-cart="checkbox"]',
      variantRow: '[js-gift-wrap="variant"]',
      productHeader: '[js-gift-wrap="product"]',
      container: '[js-gift-wrap="container"]'
    };
  }

  connectedCallback() {
    this.addEventListener('change', this.onInputChange.bind(this));
    this.updateUI();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-variant-id' && oldValue !== newValue) {
      this.updateCheckboxes(newValue);
      this.updateUI();
    }
  }

  onInputChange(event) {
    const checkbox = event.target.closest(this.selectors.checkbox);
    if (!checkbox) return;

    // Only allow one gift wrap to be selected at a time
    if (checkbox.checked) {
      this.querySelectorAll(this.selectors.checkbox).forEach(cb => {
        if (cb !== checkbox) cb.checked = false;
      });
    }

    const selectedCheckbox = Array.from(this.querySelectorAll(this.selectors.checkbox)).find(cb => cb.checked);
    const variantId = selectedCheckbox ? selectedCheckbox.dataset.variantId : null;

    // Update attribute without triggering another change event
    if (this.getAttribute('selected-variant-id') !== (variantId || '')) {
      this.setAttribute('selected-variant-id', variantId || '');
    }

    this.dispatchEvent(new CustomEvent('gift-wrap:change', {
      bubbles: true,
      detail: { variantId: variantId }
    }));

    this.updateUI();
  }

  updateCheckboxes(variantId) {
    this.querySelectorAll(this.selectors.checkbox).forEach(cb => {
      const isMatch = cb.dataset.variantId === String(variantId);
      if (cb.checked !== isMatch) {
        cb.checked = isMatch;
      }
    });
  }

  updateUI() {
    const variantId = this.getAttribute('selected-variant-id');
    const anyActive = !!variantId && variantId !== 'null' && variantId !== 'undefined';

    // Toggle hidden state for product headers (summary of details)
    this.querySelectorAll(this.selectors.productHeader).forEach(header => {
      header.classList.toggle('hidden', anyActive);
    });

    // Toggle rows
    this.querySelectorAll(this.selectors.variantRow).forEach(row => {
      const cb = row.querySelector(this.selectors.checkbox);
      const isSelected = cb && cb.dataset.variantId === String(variantId);

      if (anyActive) {
        row.classList.toggle('hidden', !isSelected);
        row.classList.toggle('is-active', isSelected);
      } else {
        row.classList.remove('hidden');
        row.classList.remove('is-active');
      }
    });

    // Toggle grid layout off when a selection is active
    this.querySelectorAll('.gift-wrap__variants-grid').forEach(grid => {
      grid.classList.toggle('has-active', anyActive);
    });

    // Ensure container is open if something is selected
    if (anyActive) {
      const container = this.querySelector(this.selectors.container);
      if (container && !container.open) {
        container.open = true;
      }
    }
  }
}

if (!customElements.get('gift-sleeves')) {
  customElements.define('gift-sleeves', GiftSleeves);
}
